import type {
  AgentDto,
  ConnectionType,
  PatchAgentBody,
  ToolKey,
} from '@/types/gtm-agents';

/**
 * The agent's switches as a write sees them, shared by the page's controls and
 * the write that takes an edit back. Pure, and it imports nothing of the
 * page, so both can use it.
 */

export interface ConnectionTool {
  key: ToolKey;
  label: string;
  group: 'read' | 'write';
}

/** The tools each connection carries, by group. */
export const CONNECTION_TOOLS: Record<
  ConnectionType,
  readonly ConnectionTool[]
> = {
  salesforce: [
    { key: 'crm_read', label: 'Read records', group: 'read' },
    { key: 'crm_write', label: 'Write records', group: 'write' },
  ],
  hubspot: [
    { key: 'crm_read', label: 'Read records', group: 'read' },
    { key: 'crm_write', label: 'Write records', group: 'write' },
  ],
  attio: [
    { key: 'crm_read', label: 'Read records', group: 'read' },
    { key: 'crm_write', label: 'Write records', group: 'write' },
  ],
  slack: [{ key: 'slack_post', label: 'Post messages', group: 'write' }],
  email: [{ key: 'send_email', label: 'Send email', group: 'write' }],
};

/**
 * A read has no one to ask in an unattended run. The API refuses an Ask each
 * time on one (a 400), and a stored one already reads as Never.
 */
export function isReadTool(tool: string): boolean {
  return Object.values(CONNECTION_TOOLS).some((tools) =>
    tools.some((item) => item.key === tool && item.group === 'read')
  );
}

/**
 * Two abilities each have a twin elsewhere on the page, and the pair is one
 * setting: Manage triggers with the Triggers tab's AI managed, Edit its
 * instructions with Clu keeps these updated.
 */
export const PAIRED_SETTING = {
  manage_triggers: 'aiManagedTriggers',
  edit_instructions: 'selfEditInstructions',
} as const satisfies Partial<Record<ToolKey, keyof AgentDto>>;

export type PairedKey = keyof typeof PAIRED_SETTING;

function isPaired(key: ToolKey): key is PairedKey {
  return key in PAIRED_SETTING;
}

/** A missing key means on; a pair is on only when both halves are. */
export function abilityOn(
  agent: Pick<AgentDto, 'tools'> &
    Partial<Pick<AgentDto, 'aiManagedTriggers' | 'selfEditInstructions'>>,
  key: ToolKey
): boolean {
  if (agent.tools[key] === false) return false;
  return isPaired(key) ? agent[PAIRED_SETTING[key]] !== false : true;
}

/** The PATCH an ability's switch writes: both halves of a pair together. */
export function abilityChange(
  current: Pick<AgentDto, 'tools'>,
  key: ToolKey,
  on: boolean
): PatchAgentBody {
  return {
    tools: { ...current.tools, [key]: on },
    ...(isPaired(key) ? { [PAIRED_SETTING[key]]: on } : {}),
  };
}
